import React, { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { PageHeader, Badge, EmptyState } from "@/components/Common";
import {
  Building2, Mail, Send, Star, MessageSquare, TrendingUp, AlertOctagon,
  Calendar as CalendarIcon, X, PhoneCall, RefreshCcw, Users,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

function ratingBar(avg) {
  if (avg == null) return null;
  const pct = Math.min(100, (Number(avg) / 5) * 100);
  return (
    <div className="mt-1.5 h-1.5 bg-gray-200 rounded-full overflow-hidden">
      <div className="h-full bg-amber-400 rounded-full transition-all" style={{ width: `${pct}%` }} />
    </div>
  );
}

function toneForRatio(complaints, total) {
  if (!total) return "slate";
  const r = complaints / total;
  if (r >= 0.5) return "red";
  if (r >= 0.25) return "amber";
  return "emerald";
}

export default function BranchFeedbackDashboard() {
  const { user } = useAuth();
  const today = new Date().toISOString().slice(0, 10);
  const [range, setRange] = useState({ from: "", to: "" });
  const [data, setData] = useState({ stats: [], range: {} });
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(null); // branch name currently being sent
  const [refreshKey, setRefreshKey] = useState(0);

  const canSendDigest = ["managing_director", "executive_director", "general_manager"]
    .includes(user?.role);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (range.from) params.append("from_date", range.from);
      if (range.to) params.append("to_date", range.to);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const { data: r } = await api.get(`/feedback-branch-stats${qs}`);
      setData(r);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [refreshKey]);

  const setPreset = (days) => {
    const to = today;
    const d = new Date(); d.setDate(d.getDate() - days);
    setRange({ from: d.toISOString().slice(0, 10), to });
  };

  const totals = useMemo(() => {
    const t = data.stats.reduce((acc, s) => ({
      total: acc.total + (s.total || 0),
      complaints: acc.complaints + (s.complaints || 0),
      compliments: acc.compliments + (s.compliments || 0),
      resolved: acc.resolved + (s.resolved || 0),
      rating_sum: acc.rating_sum + (s.avg_rating != null ? s.avg_rating * s.rating_count : 0),
      rating_count: acc.rating_count + (s.rating_count || 0),
    }), { total: 0, complaints: 0, compliments: 0, resolved: 0, rating_sum: 0, rating_count: 0 });
    return {
      ...t,
      avg_rating: t.rating_count ? (t.rating_sum / t.rating_count).toFixed(1) : "—",
    };
  }, [data]);

  const sendDigest = async (branchName) => {
    setSending(branchName);
    try {
      const params = new URLSearchParams({ hours: "24" });
      if (branchName) params.append("branch", branchName);
      const { data: r } = await api.post(`/feedback-send-digest?${params.toString()}`);
      const sent = r.sent || [], skipped = r.skipped || [];
      if (sent.length) {
        toast.success(`Digest sent to ${sent.map((s) => s.email).join(", ")}`);
      } else if (skipped.length) {
        const reasons = [...new Set(skipped.map((s) => s.reason))].join(", ");
        toast.error(`Skipped: ${reasons}. Set a manager_email on the branch first.`);
      } else {
        toast.info("Nothing to send.");
      }
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSending(null); }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Stakeholders"
        title="Branch Feedback Dashboard"
        subtitle={
          user?.role === "branch_manager"
            ? "KPIs for your branch. Set your email on the branch record to receive daily digests at 7 AM."
            : "Per-branch KPIs for regional managers. Automatic daily digest emails go out to every branch manager at 7 AM (Africa/Kampala)."
        }
        actions={
          <div className="flex gap-2">
            {canSendDigest && (
              <Button
                variant="outline" onClick={() => sendDigest(null)}
                disabled={sending !== null}
                className="border-amber-600 text-amber-800 hover:bg-amber-50"
                data-testid="branchfb-send-all-digest">
                <Send size={14} className="mr-1.5" />
                {sending === "__all__" ? "Sending…" : "Send digest to all branches"}
              </Button>
            )}
            <Button variant="outline" onClick={() => setRefreshKey((k) => k + 1)}
              className="border-emerald-900 text-emerald-900 hover:bg-emerald-50"
              data-testid="branchfb-refresh">
              <RefreshCcw size={14} className="mr-1.5" /> Refresh
            </Button>
          </div>
        }
      />

      {/* Global KPI row */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <TotalCard label="Total feedback" value={totals.total}
          icon={<MessageSquare size={14} />} tone="emerald" />
        <TotalCard label="Complaints" value={totals.complaints}
          icon={<AlertOctagon size={14} />} tone="red" />
        <TotalCard label="Compliments" value={totals.compliments}
          icon={<TrendingUp size={14} />} tone="emerald" />
        <TotalCard label="Resolved" value={totals.resolved}
          icon={<Users size={14} />} tone="slate"
          sub={totals.total ? `${Math.round(100 * totals.resolved / totals.total)}%` : ""} />
        <TotalCard label="Avg rating" value={totals.avg_rating}
          icon={<Star size={14} className="fill-amber-400 text-amber-400" />} tone="amber" />
      </div>

      {/* Date filter */}
      <div className="bg-white border border-gray-200 rounded-md p-3 mb-4 flex flex-wrap items-end gap-3"
        data-testid="branchfb-daterange">
        <div className="flex items-center gap-1.5 text-emerald-900 font-display font-semibold text-sm">
          <CalendarIcon size={14} /> Window
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest">From</Label>
          <Input type="date" value={range.from} max={range.to || undefined}
            onChange={(e) => setRange({ ...range, from: e.target.value })}
            className="mt-1 h-9 w-40" data-testid="branchfb-from-date" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-widest">To</Label>
          <Input type="date" value={range.to} min={range.from || undefined} max={today}
            onChange={(e) => setRange({ ...range, to: e.target.value })}
            className="mt-1 h-9 w-40" data-testid="branchfb-to-date" />
        </div>
        <div className="flex gap-1.5">
          {[
            { label: "Last 7d", days: 6 },
            { label: "Last 30d", days: 29 },
            { label: "Last 90d", days: 89 },
          ].map((p) => (
            <button key={p.label} type="button"
              onClick={() => setPreset(p.days)}
              className="h-9 px-3 rounded-md text-xs bg-emerald-50 border border-emerald-200 text-emerald-900 hover:bg-emerald-100 transition-colors">
              {p.label}
            </button>
          ))}
        </div>
        <Button size="sm" onClick={load} disabled={loading}
          className="h-9 bg-emerald-900 hover:bg-emerald-950 text-white"
          data-testid="branchfb-apply-filter">
          {loading ? "Loading…" : "Apply"}
        </Button>
        {(range.from || range.to) && (
          <Button variant="ghost" size="sm"
            onClick={() => { setRange({ from: "", to: "" }); setRefreshKey((k) => k + 1); }}
            className="text-red-700 hover:bg-red-50 h-9"
            data-testid="branchfb-clear-range">
            <X size={12} className="mr-1" /> Clear
          </Button>
        )}
        <div className="ml-auto">
          <Badge tone={range.from || range.to ? "emerald" : "slate"}>
            {range.from || range.to ? `${range.from || "start"} → ${range.to || today}` : "All time"}
          </Badge>
        </div>
      </div>

      {/* Per-branch grid */}
      {loading ? (
        <div className="p-10 text-center text-sm text-gray-500">Loading…</div>
      ) : data.stats.length === 0 ? (
        <EmptyState title="No branches configured"
          hint="Add branches under Administration → Branches to see per-branch KPIs." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="branchfb-grid">
          {data.stats.map((s) => (
            <BranchCard key={s.branch} s={s} canSend={canSendDigest}
              sending={sending === s.branch}
              onSend={() => sendDigest(s.branch)} />
          ))}
        </div>
      )}
    </div>
  );
}

function TotalCard({ label, value, icon, sub, tone = "slate" }) {
  const toneClass = {
    emerald: "bg-emerald-50 border-emerald-200 text-emerald-900",
    red: "bg-red-50 border-red-200 text-red-900",
    amber: "bg-amber-50 border-amber-200 text-amber-900",
    slate: "bg-gray-50 border-gray-200 text-gray-900",
  }[tone];
  return (
    <div className={`border rounded-md p-3 ${toneClass}`}>
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest font-semibold opacity-70">
        {icon} {label}
      </div>
      <div className="text-2xl font-display font-bold mt-1">{value}</div>
      {sub && <div className="text-[11px] opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
}

function BranchCard({ s, canSend, sending, onSend }) {
  const complaintTone = toneForRatio(s.complaints, s.total);
  return (
    <div className="bg-white border border-gray-200 rounded-md p-5 hover:shadow-sm transition-shadow"
      data-testid={`branchfb-card-${s.branch}`}>
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5 text-emerald-800">
            <Building2 size={14} />
            <div className="font-display font-semibold text-gray-900 text-base leading-tight truncate">
              {s.branch}
            </div>
          </div>
          {s.location && <div className="text-[11px] text-gray-500 mt-0.5">{s.location}</div>}
        </div>
        <Badge tone={complaintTone}>
          {s.total ? `${Math.round(100 * s.complaints / s.total)}% complaints` : "No data"}
        </Badge>
      </div>

      <div className="grid grid-cols-4 gap-2 mt-4 text-center">
        <MiniStat value={s.total} label="Total" />
        <MiniStat value={s.complaints} label="Complaint" tone="red" />
        <MiniStat value={s.compliments} label="Compliment" tone="emerald" />
        <MiniStat value={s.open} label="Open" tone="amber" />
      </div>

      <div className="mt-4">
        <div className="flex items-center justify-between text-xs">
          <span className="text-gray-600 flex items-center gap-1">
            <Star size={11} className="fill-amber-400 text-amber-400" /> Avg rating
          </span>
          <span className="font-semibold text-gray-900">
            {s.avg_rating != null ? s.avg_rating.toFixed(1) : "—"} / 5
            {s.rating_count > 0 && (
              <span className="text-gray-500 font-normal"> · {s.rating_count} rated</span>
            )}
          </span>
        </div>
        {ratingBar(s.avg_rating)}
      </div>

      <div className="mt-4 pt-3 border-t border-gray-100">
        <div className="flex items-center gap-1.5 text-[11px] text-gray-600 mb-1">
          <PhoneCall size={11} />
          <span className="truncate">{s.manager || "No manager listed"}</span>
        </div>
        <div className="flex items-center gap-1.5 text-[11px] text-gray-600">
          <Mail size={11} />
          {s.manager_email ? (
            <span className="truncate text-emerald-800 font-medium">{s.manager_email}</span>
          ) : (
            <span className="text-red-700 italic">
              No manager email set — daily digest will skip this branch
            </span>
          )}
        </div>
        {canSend && (
          <Button size="sm" variant="outline" onClick={onSend} disabled={sending || !s.manager_email}
            className="mt-3 w-full border-amber-600 text-amber-800 hover:bg-amber-50"
            data-testid={`branchfb-send-${s.branch}`}>
            <Send size={12} className="mr-1.5" />
            {sending ? "Sending…" : "Send digest now"}
          </Button>
        )}
      </div>
    </div>
  );
}

function MiniStat({ value, label, tone = "slate" }) {
  const toneClass = {
    red: "bg-red-50 text-red-800",
    emerald: "bg-emerald-50 text-emerald-800",
    amber: "bg-amber-50 text-amber-800",
    slate: "bg-gray-50 text-gray-800",
  }[tone];
  return (
    <div className={`rounded-md py-2 ${toneClass}`}>
      <div className="text-lg font-display font-bold leading-none">{value ?? 0}</div>
      <div className="text-[9px] uppercase tracking-widest mt-1 opacity-70">{label}</div>
    </div>
  );
}
